// Accept Collaboration Invite Page - Auto-accept for existing, signup for new
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, CheckCircle, Users, FileText, Shield } from 'lucide-react';
import { Logo } from '../components/Logo';

export function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [processing, setProcessing] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [inviteInfo, setInviteInfo] = useState(null); // Invitation details from check-invite
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // For new users
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link - no token provided');
      setProcessing(false);
      return;
    }

    handleInvitation();
  }, [token, user]);

  const handleInvitation = async () => {
    try {
      // First, check invitation status
      const checkResponse = await api.request(`/collaborators/check-invite/${token}`, {
        method: 'GET'
      });
      
      setInviteInfo(checkResponse);
      
      // If user is already logged in, auto-accept
      if (user) {
        console.log(`Auto-accepting invitation for existing user: ${user.email}`);
        await acceptInvitation();
      } else {
        // Check if target email has an existing account
        if (checkResponse.user_exists) {
          // Existing user - show "please log in" message
          console.log('Existing user detected - showing login prompt');
          setProcessing(false);
        } else {
          // New user - show signup form
          console.log('New user - showing signup form');
          setProcessing(false);
        }
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to process invitation';
      setError(errorMessage);
      setProcessing(false);
    }
  };

  const acceptInvitation = async (signupData = null) => {
    try {
      setSubmitting(true);
      setError('');

      const response = await api.request('/collaborators/accept', {
        method: 'POST',
        body: JSON.stringify({
          token: token,
          name: signupData?.name,
          password: signupData?.password
        })
      });

      setSuccess(true);
      setInviteData(response);

      // Redirect after 2 seconds
      setTimeout(() => {
        if (response.new_user) {
          // New user - redirect to login
          navigate(`/login?message=${encodeURIComponent('Account created! Please log in to access your shared document.')}`);
        } else {
          // Existing user - redirect to document
          navigate(`/editor/${response.document_id}`);
        }
      }, 2000);

    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to accept invitation';
      setError(errorMessage);
      setSubmitting(false);
      setProcessing(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    await acceptInvitation({ name, password });
  };

  // Loading state
  if (processing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <Loader2 className="animate-spin h-12 w-12 mx-auto text-purple-600 mb-4" />
            <p className="text-lg text-gray-700 font-medium">Processing your invitation...</p>
            <p className="text-sm text-gray-500 mt-2">Please wait</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <div className="mb-6 flex justify-center">
              <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {inviteData?.new_user ? 'Account Created!' : 'Invitation Accepted!'}
            </h2>
            <p className="text-gray-600 mb-4">
              {inviteData?.new_user 
                ? 'Redirecting you to login...' 
                : 'Redirecting you to the document...'}
            </p>
            <Loader2 className="animate-spin h-6 w-6 mx-auto text-purple-600" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error && !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <Logo variant="full" size="lg" className="mb-4" />
            <CardTitle className="text-red-600">Invitation Error</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button onClick={() => navigate('/')} className="w-full">
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If user exists, show "Please log in" message
  if (inviteInfo?.user_exists && !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center">
            <Logo variant="full" size="lg" className="mb-4 mx-auto" />
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 bg-purple-100 rounded-full flex items-center justify-center">
                <Users className="h-8 w-8 text-purple-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">You've Been Invited to Collaborate!</CardTitle>
            <CardDescription className="text-base mt-2">
              <strong>{inviteInfo.inviter_name}</strong> invited you to collaborate on <strong>{inviteInfo.document_title}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className="mb-6 border-blue-200 bg-blue-50">
              <AlertDescription className="text-blue-900">
                <strong>Account Detected!</strong><br />
                You already have a UPost account with <strong>{inviteInfo.invitee_email}</strong>.<br />
                Please log in to accept this invitation.
              </AlertDescription>
            </Alert>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Invitation Details
              </h3>
              <div className="text-sm text-gray-700 space-y-1">
                <p><strong>Document:</strong> {inviteInfo.document_title}</p>
                <p><strong>Role:</strong> {inviteInfo.role === 'viewer' ? 'Viewer (view & comment)' : 'Editor (full access)'}</p>
                <p><strong>From:</strong> {inviteInfo.inviter_name}</p>
              </div>
            </div>

            <Button 
              onClick={() => navigate(`/login?redirect=/accept-invite?token=${token}`)} 
              className="w-full mb-3"
            >
              Log In to Accept Invitation
            </Button>
            
            <p className="text-sm text-center text-gray-600">
              Don't remember your password?{' '}
              <a href="/login" className="text-purple-600 hover:underline">Reset it here</a>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Signup form for new users
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center">
          <Logo variant="full" size="lg" className="mb-4 mx-auto" />
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 bg-purple-100 rounded-full flex items-center justify-center">
              <Users className="h-8 w-8 text-purple-600" />
            </div>
          </div>
          <CardTitle className="text-2xl">You've Been Invited to Collaborate!</CardTitle>
          <CardDescription className="text-base mt-2">
            {inviteInfo ? (
              <>
                <strong>{inviteInfo.inviter_name}</strong> invited you to collaborate on <strong>{inviteInfo.document_title}</strong>
              </>
            ) : (
              'Someone wants to share their writing project with you on UPost'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && submitting && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <FileText className="h-5 w-5" />
              What happens next?
            </h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• You'll get a <strong>free UPost account</strong></li>
              <li>• Access the shared document to view and collaborate</li>
              <li>• Start your own writing projects with AI assistance</li>
              <li>• Get 18,400 AI words per month on the free plan</li>
            </ul>
          </div>

          <form onSubmit={handleSignup} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Your Full Name *</label>
              <Input
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={submitting}
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Create Password *</label>
              <Input
                type="password"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                required
                minLength={8}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Confirm Password *</label>
              <Input
                type="password"
                placeholder="Re-enter password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={submitting}
                required
              />
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-600 flex items-start gap-2">
                <Shield className="h-4 w-4 flex-shrink-0 mt-0.5" />
                By creating an account, you agree to UPost's terms of service. Your account is free and you can upgrade anytime.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
              size="lg"
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-5 w-5" />
                  Creating Your Account...
                </>
              ) : (
                'Create Account & Join Collaboration'
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-4">
            Already have an account?{' '}
            <a href={`/login?redirect=/accept-invite?token=${token}`} className="text-purple-600 hover:text-purple-700 font-semibold">
              Log in instead
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
